import React, { FC, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import superHostImage from 'assets/svg/superhost-user-key-indicator-for-avatar.svg';
import locationIcon from 'assets/svg/location.svg';
import closeIconInModal from 'assets/svg/close-icon-in-modal.svg';
import videoPlayIcon from 'assets/svg/video-play-icon.svg';
import Styles from 'components/chatContent/chatInfo/ChatInfo.module.scss';
import ModalShell from 'components/chatModal/ChatModalShell.module.scss';
import CustomModal from 'components/customModal/CustomModal';
import LightBox from '../../lightBox/LightBox';
import ProfileAvatar from 'components/profileAvatar/ProfileAvatar';
import { getRoomLocation, uploadIntroVideo, updateRoomIntroVideoInFirebase } from 'store/services/firebase-broadcast';
import { removeGuestFromRoom } from 'helpers/removeGuest';
import { storage } from 'utils/firebase-config';
import { ref, deleteObject } from 'firebase/storage';
import { api } from 'store/services/conversation.service';
import minus from 'assets/images/minus.png';
import Toast from 'components/toast/Tost';
import { useDispatch } from 'react-redux';
import { openImageViewer } from 'store/reducers/imageViewer.reducer';
import { getParticipantsArray, getParticipantsCount, validateIntroVideoSize, isPublicRoom } from 'helpers/utils';
import VideoPlayer from 'components/videoPlayer/VideoPlayer';

const MAX_MEDIA_DISPLAY = 9;

type MediaItem = { url: string; type: 'image' | 'video'; name?: string };

interface ChatInfoProps {
  cssClass?: string;
  info: any;
  setSelected?: any;
  allRooms?: any;

  isJoined?: boolean;
  onClose?: () => void;
  isDrawerOpen?: boolean;
}

const ChatInfo: FC<ChatInfoProps> = (props: ChatInfoProps) => {
  const { cssClass, info, allRooms, setSelected, isJoined, onClose, isDrawerOpen } = props;
  const [viewParticipants, setViewParticipants] = useState(false);
  const [viewMedia, setViewMedia] = useState(false);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [index, setIndex] = useState(0);
  const [toast, setToast] = useState(false);
  const [toastValues, setToastValues] = useState({message: '', type: ''});
  const [deleteRoom, setDeleteRoom] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const dispatch = useDispatch();

  // Validated media list. Images are kept only if they actually load; videos are
  // always kept (an <img> probe can't validate them, and the content type already
  // told us they're videos).
  const [mediaAll, setMediaAll] = useState<MediaItem[]>([]);

  // Get location data directly from Firebase room data (no API calls needed)
  const locationInfo = getRoomLocation(info);

  const loadValidMedia = async (items: MediaItem[]) => {
    const results = await Promise.all(
      items.map((m) =>
        m.type === 'video'
          ? Promise.resolve(true)
          : new Promise<boolean>((resolve) => {
              const img = new Image();
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
              img.src = m.url;
            })
      )
    );
    return items.filter((_, i) => results[i]);
  };

  useEffect(() => {
    // Prefer the type-aware `media` list (built from storage metadata). Fall back to
    // the legacy `attachments` URL array, treating every entry as an image.
    let items: MediaItem[];
    if (Array.isArray(info?.media) && info.media.length > 0) {
      items = info.media.filter(
        (m: any) => m && typeof m.url === 'string' && (m.url.startsWith('http') || m.url.startsWith('blob:'))
      );
    } else {
      const arr = Array.isArray(info?.attachments) ? info.attachments : [];
      items = arr
        .filter((u: any) => u && typeof u === 'string' && (u.startsWith('http') || u.startsWith('blob:')))
        .map((u: string) => ({ url: u, type: 'image' as const }));
    }
    loadValidMedia(items).then(setMediaAll);
  }, [info?.media, info?.attachments]);

  // Reset upload state when room changes
  useEffect(() => {
    setIsUploadingVideo(false);
    // Reset the file input when switching rooms
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  }, [info?.pk]);

  const openParticipants = () => {
    document.body.classList.add('modal-open');
    setViewParticipants(true);
  };

  const photosAndMedia = () => {
    document.body.classList.add('modal-open');
    setViewMedia(true);
  };

  const closeDialog = () => {
    document.body.classList.remove('modal-open');
    setViewParticipants(false);
    setViewMedia(false);
    setOpenLightBox(false);
  };

  // Ensure body scroll lock never sticks after unmount (e.g. closing chat info drawer).
  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const viewAllParticipants = () => (<>  
      <div className={`${Styles.pGrid} customScrollBarMuted`}>
        {getParticipantsArray(info?.participants).map((item: any, index: number) => {
          // Find the Firebase key for this participant (same logic as main list)
          const participantsObj = info?.participants || {};
          const firebaseKey = Array.isArray(info?.participants) 
            ? index 
            : Object.keys(participantsObj).find(key => participantsObj[key]?.pk === item.pk) || index;
          
          return (
            <div className={`text-center ${Styles.pInner}`} key={item.pk || firebaseKey}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <ProfileAvatar profile={item.profile} alt="participants" loading="lazy" />
                {/* Add kick button for host */}
                {info?.host?.pk === user?.pk && (
                  <img
                    onClick={() => handleDelete(item, firebaseKey)} 
                    onKeyUp={() => handleDelete(item, firebaseKey)}
                    src={minus}
                    alt="remove participant"
                    className={Styles.superHostImg}
                  />
                )}
              </div>
              <p className={`my-0 ${Styles.pName}`}>{item.name}</p>
            </div>
          );
        })}
      </div>
      <div className={ModalShell.footer}>
        <button type="button" className={ModalShell.closeBtn} onClick={() => closeDialog()}>
          Close
          <img src={closeIconInModal} alt="" className={ModalShell.closeIcon} />
        </button>
      </div>
    </>);

  const getVisibleMediaCount = () => mediaAll.length;

  const mediaCountLabel = () => {
    const photos = mediaAll.filter((m) => m.type !== 'video').length;
    const videos = mediaAll.filter((m) => m.type === 'video').length;
    const parts: string[] = [];
    parts.push(`${photos} ${photos === 1 ? 'photo' : 'photos'}`);
    if (videos > 0) parts.push(`${videos} ${videos === 1 ? 'video' : 'videos'}`);
    return parts.join(' & ');
  };

  // Open the viewer as a gallery over ALL room media, starting at the clicked index,
  // so the prev/next arrows page through the whole Photos & Media set.
  const openMedia = (index: number) =>
    dispatch(openImageViewer({
      items: mediaAll.map((m, i) => ({ url: m.url, type: m.type, name: m.name || `Media ${i + 1}` })),
      index,
    }));

  // Inner content of a media tile: a plain <img> for photos, or a poster frame
  // with a play overlay for videos. The clickable/box wrapper is supplied by each
  // caller so the same tile works in both the info-panel preview and the modal grid.
  const renderMediaTile = (item: MediaItem) =>
    item.type === 'video' ? (
      <div className={Styles.videoThumb}>
        <video src={`${item.url}#t=0.1`} preload="metadata" muted playsInline />
        <span className={Styles.playOverlay} aria-hidden="true">
          <img src={videoPlayIcon} alt="" />
        </span>
      </div>
    ) : (
      <img src={item.url} alt="media" loading="lazy" />
    );

  const ViewAllMedia = () => (
    <>
      <div className={Styles.mBody}>
        <p className={Styles.mCount}>{mediaCountLabel()}</p>
        <div className={`${Styles.mGrid} customScrollBarMuted`}>
          {(mediaAll && mediaAll.length > 0)
            ? mediaAll.map((item: MediaItem, i: number) => (
              <div
                id={`potot-${i}`}
                className={Styles.mCard}
                key={i}
                onClick={() => openMedia(i)}
                onKeyPress={() => openMedia(i)}
                role="button"
                tabIndex={0}
              >
                {renderMediaTile(item)}
              </div>
            )) : null}
        </div>
      </div>
      <div className={ModalShell.footer}>
        <button type="button" className={ModalShell.closeBtn} onClick={() => closeDialog()}>
          Close
          <img src={closeIconInModal} alt="" className={ModalShell.closeIcon} />
        </button>
      </div>
    </>
  );

    useEffect(() => {
      if (deleteRoom) {
        let updatedRoom = allRooms.find(function(e: any) {
          return e.pk == info.pk;
        })
        if (updatedRoom) {
          setSelected(updatedRoom)
        }
        setDeleteRoom(false) // Fix: Reset to false to prevent infinite loop
      }
    }, [deleteRoom, allRooms, info.pk, setSelected])

  const handleDelete = (item: any, firebaseKey: string | number) => {
    if (item?.pk == null || info?.pk == null) {
      setToastValues({
        message: 'Something went wrong!',
        type: 'Error',
      });
      setToast(true);
      return;
    }
    removeGuestFromRoom({
      userId: item.pk,
      roomId: info.pk,
      firebaseParticipantId: firebaseKey,
    })
      .then(() => {
        setDeleteRoom(true);
        setToast(true);
        setToastValues({
          message: 'User has been removed.',
          type: 'Success',
        });
      })
      .catch(() => {
        setToastValues({
          message: 'Something went wrong!',
          type: 'Error',
        });
        setToast(true);
      });
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate video file (type and size)
      const validationError = validateIntroVideoSize(file);
      if (validationError) {
        setToast(true);
        setToastValues({
          message: validationError,
          type: 'Error',
        });
        return;
      }
      
      // Upload new video
      uploadAndUpdateVideo(file);
    }
  };

  const uploadAndUpdateVideo = async (file: File) => {
    setIsUploadingVideo(true);
    let videoRef: any = null;
    let videoUrl: string | null = null;
    
    try {
      // Upload to Firebase Storage
      const uploadResult = await uploadIntroVideo(info?.pk, file);
      videoUrl = uploadResult.videoUrl;
      videoRef = uploadResult.videoRef;
      
      // Update Django backend
      await api.updateRoomIntroVideo({
        id: info?.pk,
        intro_video_url: videoUrl,
      });
      
      // Sync to Firebase Realtime Database
      await updateRoomIntroVideoInFirebase(info?.pk, videoUrl);
      
      // Optimistically update selected state for immediate UI feedback
      if (setSelected) {
        setSelected((prevSelected: any) => {
          if (prevSelected?.pk === info?.pk) {
            return {
              ...prevSelected,
              intro_video_url: videoUrl,
            };
          }
          return prevSelected;
        });
      }
      
      setToast(true);
      setToastValues({
        message: 'Intro video updated successfully.',
        type: 'Success',
      });
      
      // The Firebase update will trigger useGetRooms() to refresh automatically
      setDeleteRoom(true);
    } catch (error: any) {
      // If upload succeeded but backend update failed, clean up the uploaded file
      if (videoRef && videoUrl) {
        try {
          await deleteObject(videoRef);
          console.log('Cleaned up orphaned video file after backend update failure');
        } catch (deleteError) {
          console.error('Error cleaning up orphaned video file:', deleteError);
          // Don't throw - we still want to show the error to the user
        }
      }
      
      setToast(true);
      setToastValues({
        message: error?.message || 'Failed to update video. Please try again.',
        type: 'Error',
      });
    } finally {
      setIsUploadingVideo(false);
      // Reset input
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleRemoveVideo = async () => {
    try {
      // Delete from Firebase Storage if URL exists
      if (info?.intro_video_url) {
        try {
          // Extract path from Firebase Storage URL
          const urlObj = new URL(info.intro_video_url);
          const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
          if (pathMatch) {
            const decodedPath = decodeURIComponent(pathMatch[1]);
            const videoRef = ref(storage, decodedPath);
            await deleteObject(videoRef);
          }
        } catch (deleteError) {
          console.error('Error deleting video from storage:', deleteError);
          // Continue even if deletion fails
        }
      }
      
      // Update Django backend
      await api.updateRoomIntroVideo({
        id: info?.pk,
        intro_video_url: null as any,
      });
      
      // Sync to Firebase Realtime Database
      await updateRoomIntroVideoInFirebase(info?.pk, null);
      
      // Optimistically update selected state for immediate UI feedback
      if (setSelected) {
        setSelected((prevSelected: any) => {
          if (prevSelected?.pk === info?.pk) {
            return {
              ...prevSelected,
              intro_video_url: null,
            };
          }
          return prevSelected;
        });
      }
      
      setToast(true);
      setToastValues({
        message: 'Intro video removed successfully.',
        type: 'Success',
      });
      
      // The Firebase update will trigger useGetRooms() to refresh automatically
      setDeleteRoom(true);
    } catch (error: any) {
      setToast(true);
      setToastValues({
        message: error?.message || 'Failed to remove video. Please try again.',
        type: 'Error',
      });
    }
  };

  return (
    <div className={`${Styles.messageThreadContainer} ${isDrawerOpen ? Styles.drawerOpen : ''}`}>
      <div className={`${Styles.messageThreadWrapper} ${cssClass} customScrollBar`}>
        {onClose && (
          <button
            className={Styles.drawerCloseButton}
            onClick={onClose}
            aria-label="Close info panel"
          >
            <img src={closeIconInModal} alt="" aria-hidden="true" />
          </button>
        )}
        <div className={`text-center ${Styles.userDetails}`}>
          <div className={`position-relative ${Styles.userImageWrapper}`}>
            <ProfileAvatar profile={info?.host?.profile} alt="user img" loading="lazy" />
            {info?.host?.is_super_host && (
              <img src={superHostImage} alt="super host" className={Styles.superHostImage} />
            )}
          </div>
          <h5 className={`my-0 ${Styles.roomTopic}`}>{info?.topic}</h5>
          <span className={Styles.roomName}>{info?.host?.name}&apos;s Room</span>
          
          {/* Location Information - only show if not both Unknown */}
          {locationInfo && locationInfo.city !== "Unknown" && locationInfo.country !== "Unknown" && (
            <div className={Styles.locationInfo}>
              <span className="text-muted fontThirteen font-weight-normal">
                <img src={locationIcon} alt="location" className={Styles.locationIcon} />
                {[locationInfo.city, locationInfo.country].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
          {isPublicRoom(info, allRooms) && (
            <p className={Styles.publicRoomLine}>You can chat freely in this room</p>
          )}
        </div>
        <div className={Styles.chatParticipants}>
          <h6 className={Styles.sectionHeading}>Chat Participants ({getParticipantsCount(info?.participants)})</h6>
          <div className={Styles.participantsWrapper}>
            { getParticipantsArray(info?.participants).slice(0, 10).map((item: any, index: number) => {
              // Find the Firebase key for this participant
              const participantsObj = info?.participants || {};
              const firebaseKey = Array.isArray(info?.participants) 
                ? index 
                : Object.keys(participantsObj).find(key => participantsObj[key]?.pk === item.pk) || index;
              
              return (
                <div key={item.pk || firebaseKey}>
                  <ProfileAvatar profile={item.profile} alt="participants" loading="lazy" role="button" />
                  {
                    info?.host?.pk === user?.pk && (
                      <img
                        onClick={() => handleDelete(item, firebaseKey)} 
                        onKeyUp={() => handleDelete(item, firebaseKey)}
                        src={minus}
                        alt="super host"
                        className={Styles.superHostImg}/>
                    )
                  }
                </div>
              );
            })} 
          </div>
          {getParticipantsCount(info?.participants) > 0 && (
            <div onClick={openParticipants} onKeyPress={openParticipants} role="button" tabIndex={0}>
              <p className={`my-3 ${Styles.viewAll}`}>View All</p>
            </div>
          )}
        </div>
        <div className={Styles.photosAndMedia}>
          <h6 className={Styles.sectionHeading}>Photos & Media ({getVisibleMediaCount()})</h6>
          
          {isJoined ? (
            <div className={Styles.photosMediaWrapper}>
              {mediaAll.length > 0 && (
                mediaAll
                  .slice(0, MAX_MEDIA_DISPLAY)
                  .map((item: MediaItem, i: number) => (
                    <div
                      id={`potot-${i}`}
                      key={i}
                      onClick={() => openMedia(i)}
                      onKeyPress={() => openMedia(i)}
                      role="button"
                      tabIndex={0}
                    >
                      {renderMediaTile(item)}
                    </div>
                  ))
              )}
            </div>
          ) : (
            <div className="mt-2">
              <p className="text-muted font-weight-normal fontThirteen">Join this room to view.</p>
            </div>
          )}
        </div>
        {mediaAll.length > 0 && (
          <div onClick={photosAndMedia} onKeyPress={photosAndMedia} role="button" tabIndex={0}>
            <p className={`my-3 ${Styles.viewAll}`}>View All</p>
          </div>
        )}
        {/* Intro Video Section - Show for host (to upload) or when video exists (for all joined users) */}
        {isJoined && (info?.host?.pk === user?.pk || info?.intro_video_url) && (
          <div className={Styles.introVideoSection}>
            <div className={Styles.sectionHeader}>
              <h6 className={Styles.sectionHeading}>Intro Video</h6>
              {info?.host?.pk === user?.pk && (
                <div className={Styles.videoActions}>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoSelect}
                    style={{ display: 'none' }}
                  />
                  {info?.intro_video_url ? (
                    // Show Update/Remove buttons when video exists
                    <>
                      <button
                        onClick={() => videoInputRef.current?.click()}
                        className={Styles.editButton}
                        disabled={isUploadingVideo}
                        type="button"
                      >
                        {isUploadingVideo ? 'Uploading...' : 'Update'}
                      </button>
                      <button
                        onClick={handleRemoveVideo}
                        className={Styles.removeButton}
                        disabled={isUploadingVideo}
                        type="button"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    // Show Add button when no video exists
                    <button
                      onClick={() => videoInputRef.current?.click()}
                      className={Styles.addButton}
                      disabled={isUploadingVideo}
                      type="button"
                    >
                      {isUploadingVideo ? 'Uploading...' : 'Add'}
                    </button>
                  )}
                </div>
              )}
            </div>
            {info?.intro_video_url && (
              <div className={Styles.introVideoWrapper}>
                <VideoPlayer url={info.intro_video_url} />
              </div>
            )}
            {!info?.intro_video_url && info?.host?.pk === user?.pk && (
              <div className={Styles.noVideoPlaceholder}>
                <p className="text-muted font-weight-normal fontThirteen">
                  No intro video added yet. Click &quot;Add Intro Video&quot; to upload one.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      {viewParticipants && ReactDOM.createPortal(
        <CustomModal
          title="Chat Participants"
          wrapperClass={ModalShell.wrapper}
          modalHeaderClass={ModalShell.header}
          modalTitleClass={ModalShell.title}
          cssClass={ModalShell.body}
          closeDialog={closeDialog}
          closeModal={closeDialog}
          close={false}
        >
          {viewAllParticipants()}
        </CustomModal>,
        document.body
      )}
      {viewMedia && ReactDOM.createPortal(
        <CustomModal
          title="Photos & Media"
          wrapperClass={ModalShell.wrapper}
          modalHeaderClass={ModalShell.header}
          modalTitleClass={ModalShell.title}
          cssClass={ModalShell.body}
          closeDialog={closeDialog}
          closeModal={closeDialog}
          close={false}
        >
          {ViewAllMedia()}
        </CustomModal>,
        document.body
      )}
      {openLightBox && <div>Marjao</div> && <LightBox data={info.attachments} index={index} closeDialog={closeDialog} />}
      {toast && (
        <Toast
          message={toastValues.message}
          position="topRight"
          type={toastValues.type}
          handleCloseToast={setToast}
        />
      )}
    </div>
  );
};
export default ChatInfo;
